import { FC } from 'react';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import useRegisterUser from '@/api/useRegisterUser.ts';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';

type Schema = SignUpStepSchema;

const steps = ['choose-role', 'sign-up', 'success-sign-up'] as const;
const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="choose-role">
      <Stepper.Step<typeof steps> step="choose-role">
        <ChooseRoleStep />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="success-sign-up">
        <StepSuccessSignUp />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

function ChooseRoleStep() {
  const { goToStep, setContextData } = useStepperContext<typeof steps, Schema>();
  const handleChoose = (role: 'resident' | 'vendor') => {
    setContextData((prev) => ({ ...prev, role: role }));
    goToStep('sign-up');
  };

  return (
    <BrandCardSideContent>
      <div className="w-[343px] max-w-full flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h2 className="font-bold text-2xl">Sign in to Vendrpal.ai</h2>
          <p className="text-transparent-black font-normal">Choose your role to get started.</p>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Button
              className="rounded-full text-secondary pt-1 min-h-1 h-11"
              color="primary"
              onClick={() => handleChoose('resident')}
            >
              Resident
            </Button>
            <p className="text-sm text-text-secondary font-light italic">Book insured vendors for your building</p>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              className="rounded-full bg-white hover:bg-white/50 hover:text-secondary focus-visible pt-1 min-h-1 h-11"
              variant="outline"
              color="neutral"
              size="md"
              onClick={() => handleChoose('vendor')}
            >
              Vendor
            </Button>
            <p className="text-sm text-text-secondary font-light italic">Get approved once and win more jobs</p>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

const signUpStepSchema = z
  .object({
    email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
    terms: z.boolean().refine((value) => value, { message: 'You must agree to the terms and conditions' }),
    role: z.enum(['resident', 'vendor']),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;
function StepSignUp() {
  const { t } = useTranslation();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    defaultValues: {
      email: '',
      password: '',
      repeat_password: '',
      terms: false,
      role: contextData?.role,
    },
  });

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser) return;
        const role = data.email.endsWith('@vendrpal.ai') ? 'admin' : contextData?.role || 'resident';
        await registerUser({
          ...data,
          role: role,
        });
        setContextData(data);
        goToStep('success-sign-up');
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-[428px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleNextStep)}>
        <div className="flex flex-col gap-4">
          {isErrorRegisterUser && <Alert status="error">{errorRegisterUser.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="password"
                placeholder={t('placeholder.password')}
              />
            )}
          />
          <Controller
            control={control}
            name="repeat_password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.repeat_password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="repeat_password"
                placeholder={t('placeholder.repeat_password')}
              />
            )}
          />
          <Controller
            control={control}
            name="terms"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <CheckboxInput
                onChange={onChange}
                checked={value}
                error={errors.terms?.message}
                label={
                  <Trans i18nKey="registrationPage.agreeToTerms">
                    I accept the
                    <Link target="_blank" className="text-link underline" to={import.meta.env.VITE_TERMS_LINK}>
                      Terms and Conditions
                    </Link>
                  </Trans>
                }
              />
            )}
          />
          <LoadingButton
            className="rounded-full text-secondary"
            loading={isLoadingRegisterUser}
            type="submit"
            color="primary"
          >
            {t('button.createAccount')}
          </LoadingButton>
          <p className="text-sm text-text-secondary font-light italic">{t('registrationPage.register_info')}</p>
        </div>
        <div className="flex justify-end">
          <Link to={Routes.LOGIN}>
            <Button color="ghost">{t('button.loginToExistingAccount')}</Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
}

function StepSuccessSignUp() {
  const { t } = useTranslation();
  const { contextData } = useStepperContext<typeof steps, Schema>();

  return (
    <BrandCardSideContent>
      <div className="flex flex-col justify-center items-center gap-8 w-[343px] max-w-full">
        <div className="flex flex-col gap-4 items-center text-center">
          <h2 className="font-medium text-5xl">{t('registrationPage.congrats')}</h2>
          <p className="text-text-secondary">{t('registrationPage.congratsText')}</p>
        </div>
        <Link
          className="w-full flex flex-col"
          to={passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(contextData?.email as string),
          })}
        >
          <Button color="primary">{t('button.gotIt')}</Button>
        </Link>
      </div>
    </BrandCardSideContent>
  );
}
